import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IonPage, IonContent, IonGrid, IonRow, IonCol, IonCard, IonList, IonItem, IonLabel,
  IonButtons, IonButton, IonIcon, IonInput, IonSpinner,
} from "@ionic/react";
import {
  ellipsisVerticalOutline, copyOutline, cameraOutline, trashOutline, mailOutline, logoGoogle, logoApple, keyOutline, walletOutline,
} from "ionicons/icons";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import AppLayout from "@/components/AppLayout";
import FreshPopover from "@/components/FreshPopover";
import EditSheet from "@/components/EditSheet";
import { SettingsCard, Rows } from "@/components/SettingsCards";
import { uiAvatar } from "@/components/SidebarProfileCard";
import { NoResults } from "@/components/NotificationsDrawer";
import { FLOAT } from "@/components/SettingsRows";
import { squareDataUrl } from "@/utils/imageResize";
import { userHeaders, isUserLoggedIn } from "@/utils/userAuth";
import { useAppLive } from "@/utils/appLive";
import { t, useLanguage } from "@/utils/i18n";
import { toast } from "@/utils/toast";
import { confirmAlert } from "@/utils/confirmAlert";

import { invalidateSavedAssets, currentUserId, rememberAssetAccess, rememberActiveBusiness } from "@/components/SavedAssets";
import { clearBrandColor, forgetBrandColor } from "@/utils/brandColor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const TIER_NAMES = { starter: "Express", professional: "Pro", business: "Enterprise" }; // until the catalog loads
// A tier with no catalog name (the gifted plan, say) still reads like one: "Gift", not "gift"
const capTier = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);
const STATUS_LABELS = { active: "Active", cancelling: "Cancelling", past_due: "Past due", unpaid: "Past due", trialing: "Trial", cancelled: "Cancelled", canceled: "Cancelled", expired: "Expired" };
const STATUS_COLORS = { active: "admin-badge-green", trialing: "admin-badge-amber", cancelling: "admin-badge-amber", past_due: "admin-badge-red", unpaid: "admin-badge-red" };

// The native app sells nothing itself (store rules): its plan and balance
// buttons open the website's billing page in the browser
const SITE_URL = (process.env.REACT_APP_PUBLIC_SITE_URL || "https://mintslip.com").replace(/\/$/, "");
const NATIVE = Capacitor.isNativePlatform?.() || false;
const openSiteBilling = async (segment) => {
  const url = `${SITE_URL}/app/billing?${segment ? `segment=${segment}&` : ""}from=app`;
  try { await Browser.open({ url }); } catch { try { window.open(url, "_blank"); } catch { /* nothing to do */ } }
};
const readStored = () => { try { return JSON.parse(localStorage.getItem("userInfo") || "null") || {}; } catch { return {}; } };
// The sidebar card and the top-bar title read the account from storage
const syncStored = (u) => {
  try {
    const cur = readStored();
    localStorage.setItem("userInfo", JSON.stringify({ ...cur, id: u.id, email: u.email, name: u.name || "", photo: u.photo || "", subscription: u.subscription ?? cur.subscription ?? null, preferences: u.preferences || cur.preferences || {} }));
  } catch { /* storage unavailable */ }
  window.dispatchEvent(new CustomEvent("mintslip-user-updated"));
};

// /app/account — the signed-in person's own page, laid out like a user's page
// in the admin: photo card, Account Info (edit sheet), Authentication Methods
// (the email code, plus Google / Apple to connect or disconnect, as on the
// admin's Settings page), Subscription, then Sign out and Delete account.
export default function AppAccount() {
  const navigate = useNavigate();
  const location = useLocation();
  useLanguage();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [guest, setGuest] = useState(!isUserLoggedIn());
  const [status, setStatus] = useState(null); // /api/stripe/subscription-status
  const [tierNames, setTierNames] = useState(TIER_NAMES); // as the admin named the plans
  const [billingEnabled, setBillingEnabled] = useState(true);
  const [providers, setProviders] = useState({ google: false, apple: false }); // which sign-in providers the server has configured
  const [menu, setMenu] = useState({ open: false, event: undefined });
  const [sheet, setSheet] = useState(null); // "info"

  // A gift bell links here with #downloads: scroll the subscription card
  // (plan and downloads) into view and flash its outline once rendered
  useEffect(() => {
    if ((location.hash || "") !== "#downloads") return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector(".account-subscription-card");
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("is-target");
      setTimeout(() => el.classList.remove("is-target"), 1800);
    }, 400);
    return () => clearTimeout(t);
  }, [location.hash, user]); // eslint-disable-line
  const [info, setInfo] = useState({ name: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);

  const signOutLocally = useCallback(() => {
    // The brand colour and the shared asset cache belong to the account that
    // is leaving, so they go with it rather than waiting for a remount.
    const uid = currentUserId();
    localStorage.removeItem("userToken");
    localStorage.removeItem("userInfo");
    forgetBrandColor("app", uid);
    clearBrandColor();
    rememberAssetAccess(uid, false);
    rememberActiveBusiness(uid, null);
    invalidateSavedAssets();
    window.dispatchEvent(new CustomEvent("mintslip-user-updated"));
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/me`, { headers: userHeaders() });
      if (res.status === 401) { signOutLocally(); setGuest(true); return null; }
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.user) { setUser(data.user); syncStored(data.user); return data.user; }
    } catch { /* keep what we have */ }
    return null;
  }, [signOutLocally]);
  const loadStatus = useCallback(async () => {
    try {
      const d = await (await fetch(`${BACKEND_URL}/api/stripe/subscription-status`, { headers: userHeaders() })).json();
      setStatus(d && d.success ? d : { hasSubscription: false });
    } catch { setStatus({ hasSubscription: false }); }
  }, []);
  const refresh = useCallback(() => { if (!isUserLoggedIn()) { setGuest(true); return; } load(); loadStatus(); }, [load, loadStatus]);

  useEffect(() => {
    refresh();
    fetch(`${BACKEND_URL}/api/app-settings`).then((r) => (r.ok ? r.json() : null)).then((d) => { if (d && d.success) setBillingEnabled(d.settings?.billingLink !== false); }).catch(() => {});
    fetch(`${BACKEND_URL}/api/user/login/config`).then((r) => (r.ok ? r.json() : null)).then((d) => { if (d && d.success) setProviders({ google: !!d.google, apple: !!d.apple }); }).catch(() => {});
    fetch(`${BACKEND_URL}/api/subscriptions/catalog`).then((r) => (r.ok ? r.json() : null)).then((d) => { if (d && d.success && d.plans) setTierNames((p) => ({ ...p, ...Object.fromEntries(d.plans.map((x) => [x.id, x.name])) })); }).catch(() => {});
  }, [refresh]);
  useAppLive(() => { refresh(); }); // a plan change shows up at once

  // Back from a provider: ?linked=google|apple or ?error=…
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const linked = params.get("linked");
    const error = params.get("error");
    if (!linked && !error) return;
    navigate("/app/account", { replace: true });
    if (linked) toast.success(`${linked === "apple" ? "Apple" : "Google"} ${t("account connected")}`);
    if (error) toast.error(error);
  }, [location.search]); // eslint-disable-line

  const call = async (path, method, body) => {
    const res = await fetch(`${BACKEND_URL}${path}`, { method, headers: userHeaders(body ? { "Content-Type": "application/json" } : {}), body: body ? JSON.stringify(body) : undefined });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.detail || t("Something went wrong"));
    return data;
  };
  const attempt = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); return true; } catch (err) { toast.error(err.message); return false; } };
  const copy = async (text, what) => { try { await navigator.clipboard.writeText(text); toast.success(t(`${what} copied`)); } catch { toast.error(t("Could not copy")); } };
  const putProfile = async (body) => { const d = await call("/api/user/profile", "PUT", body); if (d.user) { setUser(d.user); syncStored(d.user); } };

  // ── photo ──
  const onPhotoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(file.type)) { toast.error(t("Use a PNG, JPG, WebP or GIF.")); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error(t("Image must be under 4 MB.")); return; }
    try { const photo = await squareDataUrl(file, 512); await attempt(() => putProfile({ photo }), t("Photo updated")); } catch (err) { toast.error(err.message); }
  };

  // ── sheets ──
  const openInfo = () => { setInfo({ name: user.name || "", phone: user.phone || "" }); setSheet("info"); };
  const saveSheet = async () => {
    setSaving(true);
    try {
      if (!info.name.trim()) { toast.error(t("Enter your name.")); return; }
      const ok = await attempt(() => putProfile({ name: info.name.trim(), phone: info.phone.trim() }), t("Account info updated"));
      if (ok) setSheet(null);
    } finally {
      setSaving(false);
    }
  };

  // ── Google / Apple (the admin Settings flow) ──
  const connect = async (provider) => {
    setBusy(provider);
    try {
      const data = await call(`/api/user/auth/link/${provider}`, "POST");
      if (!data.url) throw new Error(t("Could not start the connection"));
      window.location.href = data.url; // the provider bounces back to ?linked=…
    } catch (err) {
      toast.error(err.message);
      setBusy(null);
    }
  };
  const disconnect = async (provider) => {
    const label = provider === "apple" ? "Apple" : "Google";
    if (!(await confirmAlert({ header: `${t("Disconnect")} ${label}?`, message: `${t("You will no longer be able to sign in with this")} ${label} ${t("account. Your email code still works.")}`, confirmText: t("Disconnect") }))) return;
    setBusy(provider);
    await attempt(async () => { await call(`/api/user/auth/link/${provider}`, "DELETE"); await load(); }, `${label} ${t("disconnected")}`);
    setBusy(null);
  };

  // ── actions ──
  const signOut = async () => {
    setBusy("signout");
    try { await fetch(`${BACKEND_URL}/api/user/logout`, { method: "POST", headers: userHeaders() }); } catch { /* the token is dropped either way */ }
    signOutLocally();
    setBusy(null);
    navigate("/app");
  };
  const deleteAccount = async () => {
    if (!(await confirmAlert({ header: t("Delete your account?"), message: t("Your account, saved documents access and sign-ins are removed. This cannot be undone."), confirmText: t("Delete") }))) return;
    setBusy("delete");
    const ok = await attempt(async () => { await call("/api/user/me", "DELETE"); }, t("Account deleted"));
    setBusy(null);
    if (ok) { signOutLocally(); navigate("/app"); }
  };

  const copyBtn = (text, what) => (
    <IonButtons slot="end">
      <IonButton color="dark" size="small" fill="clear" onClick={() => copy(text, what)} aria-label={`Copy ${what.toLowerCase()}`}>
        <IonIcon slot="icon-only" icon={copyOutline} />
      </IonButton>
    </IonButtons>
  );

  const name = user?.name || user?.email || t("Account");
  const google = user?.oauth?.google;
  const apple = user?.oauth?.apple;
  // Subscription: Stripe's live status first, the account record as the fallback
  const sub = status?.hasSubscription
    ? { tier: status.tier, status: status.cancelAtPeriodEnd ? "cancelling" : status.status, remaining: status.downloadsRemaining, end: status.currentPeriodEnd }
    : user?.subscription && ["active", "trialing", "cancelling", "past_due"].includes(user.subscription.status)
      ? { tier: user.subscription.tier, status: user.subscription.status, remaining: user.subscription.downloads_remaining, total: user.subscription.downloads_total, end: user.subscription.currentPeriodEnd }
      : null;
  return (
    <AppLayout>
      <IonPage>
        <IonContent>
          <div className="admin-profile app-account" style={{ maxWidth: 1200, margin: "0 auto", padding: "16px 16px 40px" }}>
            {guest ? (
              <div className="app-account-guest">
                <NoResults text={t("Sign in to see your account")} />
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <IonButton onClick={() => navigate("/login")}>{t("Sign in")}</IonButton>
                </div>
              </div>
            ) : !user ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><IonSpinner name="crescent" /></div>
            ) : (
              <IonGrid>
                <IonRow>
                  <IonCol size="12" sizeSm="4">
                    <IonCard className="profile-photo-card">
                      <img alt="" src={user.photo || uiAvatar(name)} />
                      <IonButtons className="image-buttons">
                        <IonButton size="small" fill="clear" aria-label={t("Photo options")} onClick={(e) => setMenu({ open: true, event: e.nativeEvent })}>
                          <IonIcon slot="icon-only" size="small" icon={ellipsisVerticalOutline} />
                        </IonButton>
                      </IonButtons>
                    </IonCard>
                    <FreshPopover open={menu.open} event={menu.event} onClose={() => setMenu({ open: false, event: undefined })} side="bottom" alignment="end" className="profile-photo-menu">
                      <IonList lines="none">
                        <IonItem button detail={false} onClick={() => { setMenu({ open: false, event: undefined }); fileInputRef.current?.click(); }}>
                          <IonIcon slot="start" icon={cameraOutline} />
                          <IonLabel>{t("Upload photo")}</IonLabel>
                        </IonItem>
                        {user.photo && (
                          <IonItem button detail={false} onClick={() => { setMenu({ open: false, event: undefined }); attempt(() => putProfile({ photo: "" }), t("Photo removed")); }}>
                            <IonIcon slot="start" icon={trashOutline} color="danger" />
                            <IonLabel color="danger">{t("Remove photo")}</IonLabel>
                          </IonItem>
                        )}
                      </IonList>
                    </FreshPopover>
                    <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onPhotoFile} />
                  </IonCol>

                  <IonCol size="12" sizeSm="8">
                    <SettingsCard title={t("Account Info")} className="account-info-card" onEdit={openInfo}>
                      <Rows rows={[
                        [t("Name"), user.name, user.name ? copyBtn(user.name, "Name") : null],
                        [t("Email"), user.email, copyBtn(user.email, "Email")],
                        [t("Phone Number"), user.phone, user.phone ? copyBtn(user.phone, "Phone number") : null],
                        [t("Member since"), user.createdAt ? new Date(user.createdAt).toLocaleDateString() : ""],
                      ]} />
                    </SettingsCard>

                    <SettingsCard title={t("Authentication Methods")} className="account-auth-card">
                      <IonList>
                        <IonItem>
                          <IonIcon slot="start" icon={mailOutline} />
                          <IonLabel className="ion-text-wrap">{t("Email code")}<p>{t("A one-time code sent")}</p></IonLabel>
                        </IonItem>
                        <IonItem>
                          <IonIcon slot="start" icon={logoGoogle} style={{ color: "#EA4335" }} />
                          <IonLabel className="ion-text-wrap">
                            Google
                            {google && <p>{t("Connected to")} {google.email || user.email}</p>}
                          </IonLabel>
                          {google ? (
                            <IonButton slot="end" color="danger" fill="clear" size="small" className="profile-action" disabled={busy === "google"} onClick={() => disconnect("google")}>{t("Disconnect")}</IonButton>
                          ) : (
                            <IonButton slot="end" color="success" fill="clear" size="small" className="profile-action" disabled={!providers.google || busy === "google"} onClick={() => connect("google")}>{t("Connect")}</IonButton>
                          )}
                        </IonItem>
                        <IonItem lines="none">
                          <IonIcon slot="start" icon={logoApple} color="dark" />
                          <IonLabel className="ion-text-wrap">
                            Apple
                            {apple && <p>{t("Connected to")} {apple.email || user.email}</p>}
                          </IonLabel>
                          {apple ? (
                            <IonButton slot="end" color="danger" fill="clear" size="small" className="profile-action" disabled={busy === "apple"} onClick={() => disconnect("apple")}>{t("Disconnect")}</IonButton>
                          ) : (
                            <IonButton slot="end" color="success" fill="clear" size="small" className="profile-action" disabled={!providers.apple || busy === "apple"} onClick={() => connect("apple")}>{t("Connect")}</IonButton>
                          )}
                        </IonItem>
                      </IonList>
                    </SettingsCard>

                    <SettingsCard
                      title={t("Subscription")}
                      className="account-subscription-card"
                      action={billingEnabled ? (
                        <IonButton color="dark" fill="clear" className="profile-action" onClick={() => (NATIVE ? openSiteBilling() : navigate("/app/billing"))}>
                          <IonIcon icon={keyOutline} slot="start" /> {sub ? t("Manage") : t("See plans")}
                        </IonButton>
                      ) : null}
                    >
                      <Rows rows={[
                        [t("Plan"), sub ? (tierNames[sub.tier] || capTier(sub.tier)) : t("None")],
                        sub && [t("Status"), <span className={`admin-badge ${STATUS_COLORS[sub.status] || "admin-badge-slate"}`}>{STATUS_LABELS[sub.status] || sub.status}</span>],
                        sub && [t("Downloads"), sub.remaining === -1 ? t("Unlimited") : sub.total ? `${sub.remaining ?? 0} / ${sub.total}` : `${sub.remaining ?? 0} ${t("remaining")}`],
                        sub && sub.end && [sub.status === "cancelling" ? t("Ends") : t("Renews"), new Date(sub.end).toLocaleDateString()],
                        // Gifted downloads live on the account, not on a plan
                        !sub && (user?.bonusDownloads || 0) > 0 && [t("Account downloads"), `${user.bonusDownloads} ${t("remaining")}`],
                      ]} />
                    </SettingsCard>

                    {/* The loaded balance documents spend their price from once
                        the plan's downloads run out. It's topped up on the web
                        billing page (the native app links out there). */}
                    <SettingsCard
                      title={t("Wallet Balance")}
                      className="account-wallet-card"
                      action={billingEnabled ? (
                        <IonButton color="dark" fill="clear" className="profile-action" onClick={() => (NATIVE ? openSiteBilling("wallet") : navigate("/app/billing?segment=wallet"))}>
                          <IonIcon icon={walletOutline} slot="start" /> {t("Add funds")}
                        </IonButton>
                      ) : null}
                    >
                      <Rows rows={[
                        [t("Balance"), `$${((user?.walletCents || 0) / 100).toFixed(2)}`],
                        [t("Used for"), t("Documents, once your downloads run out")],
                      ]} />
                    </SettingsCard>

                    <IonButton expand="block" color="light" disabled={busy === "signout"} onClick={signOut} style={{ margin: "0 0 12px" }}>{t("Sign out")}</IonButton>
                    <IonButton expand="block" color="danger" disabled={busy === "delete"} onClick={deleteAccount} style={{ margin: "0 0 24px" }}>{t("Delete account")}</IonButton>
                  </IonCol>
                </IonRow>
              </IonGrid>
            )}
          </div>
        </IonContent>
      </IonPage>

      {sheet === "info" && (
        <EditSheet title={t("Account Info")} onClose={() => setSheet(null)} onSave={saveSheet} saving={saving}>
          <IonInput {...FLOAT} label={t("Name")} value={info.name} onIonInput={(e) => setInfo((p) => ({ ...p, name: e.detail.value ?? "" }))} />
          <IonInput {...FLOAT} label={t("Phone Number")} type="tel" inputmode="tel" value={info.phone} onIonInput={(e) => setInfo((p) => ({ ...p, phone: e.detail.value ?? "" }))} />
          <p className="admin-profile-sheet-note">{t("Your email is")} {user?.email}. {t("Contact support to change it.")}</p>
        </EditSheet>
      )}
    </AppLayout>
  );
}
